/**
 * LUKHAS.AI SSO Client
 *
 * Handles authentication via lukhas.id SSO:
 * - Redirects to lukhas.id for login
 * - Validates SSO tokens on callback
 * - Manages local session state
 *
 * All authentication logic lives in lukhas.id - this client
 * simply integrates with the SSO flow.
 */

const LUKHAS_ID_URL = import.meta.env.VITE_LUKHAS_ID_URL || 'https://lukhas.id'
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || 'https://api.lukhas.id/v1'

// Session storage key
const SESSION_KEY = 'lukhas_sso_session'

/** Guard against concurrent refresh attempts. */
let _refreshInFlight: Promise<SSOSession | null> | null = null

export type AccessState = 'anonymous' | 'pending' | 'approved' | 'admin'

export interface SSOUser {
  id: string
  email: string
  lambdaId: string  // ΛiD# - unique user identifier (e.g., "ΛiD#7842")
  displayName: string
  avatar?: string
  roles?: string[]
  tier?: 'free' | 'pro' | 'team' | 'enterprise'
  organization?: string
  createdAt?: string
}

/**
 * Derive access state from user roles and tier.
 * Returns 'anonymous' when no user is provided.
 */
export function deriveAccessState(user: SSOUser | null): AccessState {
  if (!user) return 'anonymous'

  const roles = user.roles ?? []

  if (roles.includes('admin')) return 'admin'
  if (roles.includes('developer') || roles.includes('approved') || user.tier === 'pro' || user.tier === 'team' || user.tier === 'enterprise') {
    return 'approved'
  }

  return 'pending'
}

export interface SSOSession {
  user: SSOUser
  accessToken: string
  expiresAt: number
}

/**
 * Get current session from memory/sessionStorage
 */
export function getSession(): SSOSession | null {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (!stored) return null

    const session: SSOSession = JSON.parse(stored)

    // Check if session is expired
    if (Date.now() > session.expiresAt) {
      clearSession()
      return null
    }

    return session
  } catch {
    return null
  }
}

/**
 * Store session in sessionStorage
 */
function setSession(session: SSOSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

/**
 * Clear session
 */
export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY)
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getSession() !== null
}

/**
 * Get current user
 */
export function getCurrentUser(): SSOUser | null {
  const session = getSession()
  return session?.user ?? null
}

/**
 * Redirect to lukhas.id for login
 * After login, lukhas.id will redirect back with SSO token
 */
export function redirectToLogin(returnPath?: string): void {
  const returnUrl = returnPath
    ? `${window.location.origin}${returnPath}`
    : window.location.href

  // Build SSO URL - lukhas.id will handle auth and redirect back
  const ssoUrl = new URL(`${LUKHAS_ID_URL}/sso/authorize`)
  ssoUrl.searchParams.set('client_id', 'lukhas_ai')
  ssoUrl.searchParams.set('redirect_uri', `${window.location.origin}/sso/callback`)
  ssoUrl.searchParams.set('return_to', returnUrl)
  ssoUrl.searchParams.set('response_type', 'token')

  window.location.href = ssoUrl.toString()
}

/**
 * Handle SSO callback - validate token and establish session
 * Called from /sso/callback route
 */
export async function handleSSOCallback(): Promise<SSOSession> {
  // Get token from URL hash or query params
  const params = new URLSearchParams(window.location.hash.slice(1) || window.location.search)
  const token = params.get('token') || params.get('access_token')
  const returnTo = params.get('return_to') || '/'

  if (!token) {
    throw new Error('No SSO token received')
  }

  // Validate token with lukhas.id API
  const response = await fetch(`${AUTH_API_URL}/sso/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'SSO validation failed' }))
    throw new Error(error.message || 'SSO validation failed')
  }

  const data = await response.json()

  // Create session
  const session: SSOSession = {
    user: data.user,
    accessToken: data.tokens.accessToken,
    expiresAt: Date.now() + (data.tokens.expiresIn * 1000),
  }

  setSession(session)

  // Redirect to original destination
  window.location.href = returnTo

  return session
}

/**
 * Logout - clear local session and optionally redirect to lukhas.id logout
 */
export async function logout(redirectToLogout = true): Promise<void> {
  const session = getSession()

  // Clear local session
  clearSession()

  if (redirectToLogout && session) {
    // Redirect to lukhas.id logout (will invalidate server session)
    const logoutUrl = new URL(`${LUKHAS_ID_URL}/logout`)
    logoutUrl.searchParams.set('return_to', window.location.origin)
    window.location.href = logoutUrl.toString()
  }
}

/**
 * Attempt to silently refresh the session using the HttpOnly refresh cookie.
 * Safe to call concurrently — only one request is made.
 */
export async function refreshSession(): Promise<SSOSession | null> {
  if (_refreshInFlight) return _refreshInFlight

  _refreshInFlight = _doRefresh()
  try {
    return await _refreshInFlight
  } finally {
    _refreshInFlight = null
  }
}

async function _doRefresh(): Promise<SSOSession | null> {
  try {
    const response = await fetch(`${AUTH_API_URL}/sso/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) return null

    const data = await response.json()

    const session: SSOSession = {
      user: data.user,
      accessToken: data.tokens.accessToken,
      expiresAt: Date.now() + (data.tokens.expiresIn * 1000),
    }

    setSession(session)
    return session
  } catch {
    return null
  }
}

/**
 * Make authenticated API request.
 * On 401: attempts silent refresh, retries once. On failure: clears session.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const session = getSession()

  if (!session) {
    const refreshed = await refreshSession()
    if (!refreshed) {
      throw new Error('Not authenticated')
    }
    return _doAuthFetch(input, init, refreshed.accessToken)
  }

  const response = await _doAuthFetch(input, init, session.accessToken)

  if (response.status === 401) {
    const refreshed = await refreshSession()
    if (refreshed) {
      return _doAuthFetch(input, init, refreshed.accessToken)
    }
    clearSession()
    throw new Error('Session expired or revoked')
  }

  return response
}

async function _doAuthFetch(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  accessToken: string,
): Promise<Response> {
  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${accessToken}`)

  return fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  })
}
